import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { useSite } from '../../context/SiteContext';
import { getBusinessDateISO } from '../../utils/businessDate';
import { isConversionMachine } from '../../utils/conversionMachines';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

const dateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getBusinessDate = () => {
  const [year, month, day] = getBusinessDateISO().split('-').map(Number);
  return new Date(year, month - 1, day);
};

const initialDates = () => {
  const end = getBusinessDate();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start_date: dateInputValue(start), end_date: dateInputValue(end) };
};

const number = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat('es-MX', { maximumFractionDigits }).format(Number(value) || 0);

const shortDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short' }).format(new Date(year, month - 1, day));
};

const compactNumber = (value) =>
  new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value) || 0);

export const scrapPercent = (summary = {}) => {
  const scrap = Number(summary.scrap) || 0;
  const total = (Number(summary.production) || 0) + scrap;
  return total > 0 ? (scrap / total) * 100 : 0;
};

const KpiCard = ({ label, value, detail, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className={`inline-flex rounded-lg border px-2 py-1 text-[11px] font-bold uppercase tracking-wide ${tones[tone]}`}>
        {label}
      </div>
      <div className="mt-3 text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{detail}</div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <section className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
    <div className="mb-4">
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
    </div>
    <div className="h-72">{children}</div>
  </section>
);

const emptyResponse = {
  summary: {},
  by_day: [],
  by_week: [],
  by_machine: [],
  by_shift: [],
  by_packing_group: [],
  by_bag_style: [],
  by_bag_size_group: [],
};

const ConversionAnalytics = () => {
  const { currentSite, getCurrentSiteId } = useSite();
  const siteId = currentSite?.id || getCurrentSiteId();
  const [filters, setFilters] = useState(() => ({ ...initialDates(), line_id: '', shift_id: '' }));
  const [catalogs, setCatalogs] = useState({ lines: [], shifts: [] });
  const [analytics, setAnalytics] = useState(emptyResponse);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;

    axios.get('/catalogs/all', { params: { site_id: siteId } })
      .then(({ data }) => {
        if (cancelled) return;
        setCatalogs({
          lines: [...(data.lines || [])].filter(isConversionMachine).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
          shifts: [...(data.shifts || [])].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
        });
      })
      .catch(() => {
        if (!cancelled) setCatalogs({ lines: [], shifts: [] });
      });

    return () => { cancelled = true; };
  }, [siteId]);

  const loadAnalytics = useCallback(async (activeFilters = filters) => {
    if (!siteId) return;
    try {
      setLoading(true);
      setError('');
      const params = {
        site_id: siteId,
        start_date: activeFilters.start_date,
        end_date: activeFilters.end_date,
      };
      if (activeFilters.line_id) params.line_id = activeFilters.line_id;
      if (activeFilters.shift_id) params.shift_id = activeFilters.shift_id;

      const { data } = await axios.get('/conversion/analytics', { params });
      setAnalytics({
        ...emptyResponse,
        ...data,
        by_day: Array.isArray(data.by_day) ? data.by_day : [],
        by_week: Array.isArray(data.by_week) ? data.by_week : [],
        by_machine: Array.isArray(data.by_machine) ? data.by_machine : [],
        by_shift: Array.isArray(data.by_shift) ? data.by_shift : [],
        by_packing_group: Array.isArray(data.by_packing_group) ? data.by_packing_group : [],
        by_bag_style: Array.isArray(data.by_bag_style) ? data.by_bag_style : [],
        by_bag_size_group: Array.isArray(data.by_bag_size_group) ? data.by_bag_size_group : [],
      });
    } catch (requestError) {
      setAnalytics(emptyResponse);
      setError(requestError.response?.data?.message || requestError.response?.data?.detail || 'No fue posible cargar el análisis.');
    } finally {
      setLoading(false);
    }
  }, [filters, siteId]);

  useEffect(() => {
    loadAnalytics();
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (preset) => {
    const end = getBusinessDate();
    const start = new Date(end);

    if (preset === 'today') {
      const businessDate = dateInputValue(end);
      const next = { ...filters, start_date: businessDate, end_date: businessDate };
      setFilters(next);
      loadAnalytics(next);
      return;
    }
    if (preset === '7d') start.setDate(end.getDate() - 6);
    if (preset === '30d') start.setDate(end.getDate() - 29);
    if (preset === 'week') {
      const mondayOffset = (end.getDay() + 6) % 7;
      start.setDate(end.getDate() - mondayOffset);
    }
    if (preset === 'month') start.setDate(1);

    const next = { ...filters, start_date: dateInputValue(start), end_date: dateInputValue(end) };
    setFilters(next);
    loadAnalytics(next);
  };

  const submit = (event) => {
    event.preventDefault();
    loadAnalytics(filters);
  };

  const summary = analytics.summary || {};
  const hasData = (summary.reports || 0) > 0;
  const appliedFilters = analytics.filters || filters;
  const isSingleDay = appliedFilters.start_date === appliedFilters.end_date;

  const dailyData = useMemo(() => ({
    labels: analytics.by_day.map((item) => shortDate(item.key)),
    datasets: [
      {
        label: 'Producción',
        data: analytics.by_day.map((item) => item.production),
        borderColor: '#2563eb',
        backgroundColor: isSingleDay ? '#2563eb' : 'rgba(37, 99, 235, 0.12)',
        pointBackgroundColor: '#2563eb',
        borderRadius: isSingleDay ? 8 : 0,
        maxBarThickness: isSingleDay ? 90 : undefined,
        tension: 0.28,
        fill: true,
        yAxisID: 'y',
      },
      {
        label: 'Scrap',
        data: analytics.by_day.map((item) => item.scrap),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderRadius: isSingleDay ? 8 : 0,
        maxBarThickness: isSingleDay ? 90 : undefined,
        tension: 0.28,
        yAxisID: 'yScrap',
      },
    ],
  }), [analytics.by_day, isSingleDay]);

  const barData = (items, color = '#2563eb') => {
    const safeItems = Array.isArray(items) ? items : [];

    return {
      labels: safeItems.map((item) => item.label),
      datasets: [{
        label: 'Producción',
        data: safeItems.map((item) => item.production),
        backgroundColor: color,
        borderRadius: 7,
        maxBarThickness: 42,
      }],
    };
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${number(context.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#64748b' } },
      y: { beginAtZero: true, ticks: { callback: compactNumber, color: '#64748b' }, grid: { color: '#e2e8f0' } },
    },
  };

  const dailyOptions = {
    ...commonOptions,
    scales: {
      ...commonOptions.scales,
      yScrap: {
        beginAtZero: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: { callback: compactNumber, color: '#b45309' },
      },
    },
  };

  const machineOptions = {
    ...commonOptions,
    indexAxis: 'y',
    plugins: { ...commonOptions.plugins, legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { callback: compactNumber, color: '#64748b' }, grid: { color: '#e2e8f0' } },
      y: { grid: { display: false }, ticks: { color: '#334155', font: { weight: 'bold' } } },
    },
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1600px] space-y-5">
        <header className="rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-blue-800 px-6 py-6 text-white shadow-lg">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200">Conversión</div>
              <h1 className="mt-2 text-2xl font-extrabold md:text-3xl">Análisis de Producción</h1>
              <p className="mt-2 max-w-2xl text-sm text-blue-100">
                Producción, scrap y desempeño comparados por periodo, máquina y turno.
              </p>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-right backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-wide text-blue-200">Sitio</div>
              <div className="mt-1 font-bold">{currentSite?.name || currentSite?.code || 'Sitio actual'}</div>
            </div>
          </div>
        </header>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Desde
              <input
                type="date"
                value={filters.start_date}
                max={filters.end_date}
                onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </label>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Hasta
              <input
                type="date"
                value={filters.end_date}
                min={filters.start_date}
                onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </label>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Máquina
              <select
                value={filters.line_id}
                onChange={(event) => setFilters((current) => ({ ...current, line_id: event.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Todas las máquinas</option>
                {catalogs.lines.map((line) => <option key={line.id} value={line.id}>{line.code} - {line.name}</option>)}
              </select>
            </label>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Turno
              <select
                value={filters.shift_id}
                onChange={(event) => setFilters((current) => ({ ...current, shift_id: event.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Todos los turnos</option>
                {catalogs.shifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.code} - {shift.name}</option>)}
              </select>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="self-end rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-blue-800 disabled:cursor-wait disabled:bg-slate-400"
            >
              {loading ? 'Actualizando…' : 'Aplicar filtros'}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              ['today', 'Hoy'],
              ['7d', 'Últimos 7 días'],
              ['30d', 'Últimos 30 días'],
              ['week', 'Semana actual'],
              ['month', 'Mes actual'],
            ].map(([key, label]) => (
              <button key={key} type="button" onClick={() => applyPreset(key)} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800">
                {label}
              </button>
            ))}
            <span className="self-center px-1 text-xs text-slate-500">Día operativo: 07:00–06:59</span>
          </div>
        </form>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{error}</div>}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-8">
          <KpiCard label="Producción" value={number(summary.production)} detail={`${number(summary.reports)} reportes`} />
          <KpiCard label="% de scrap" value={`${number(scrapPercent(summary), 2)}%`} detail="Scrap / (producción buena + scrap)" tone="violet" />
          <KpiCard label="Scrap" value={number(summary.scrap)} detail={`${number(summary.quality_pct, 2)}% calidad`} tone="amber" />
          <KpiCard label="Uptime" value={`${number(summary.uptime_pct, 2)}%`} detail={`${number(summary.runtime_min)} min runtime`} tone="emerald" />
          <KpiCard label="Tiempo muerto" value={`${number(summary.downtime_pct, 2)}%`} detail={`${number(summary.downtime_min)} minutos`} tone="amber" />
          <KpiCard label="BPM" value={number(summary.bpm, 2)} detail="Bolsas por minuto" tone="blue" />
          <KpiCard label="OEE-lite" value={`${number(summary.oee_lite_pct, 2)}%`} detail="Uptime × calidad" tone="emerald" />
          <KpiCard label="Cerrados" value={`${number(summary.closed_reports)} / ${number(summary.reports)}`} detail="Reportes cerrados" tone="slate" />
        </div>

        {loading && !hasData ? (
          <div className="rounded-2xl border border-slate-200 bg-white py-20 text-center text-sm font-semibold text-slate-500 shadow-sm">Preparando análisis…</div>
        ) : !hasData ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center shadow-sm">
            <div className="text-lg font-bold text-slate-800">No hay reportes en este periodo</div>
            <div className="mt-2 text-sm text-slate-500">Prueba ampliando las fechas o retirando los filtros de máquina y turno.</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <ChartCard
                title={isSingleDay ? 'Producción del día operativo' : 'Producción por día'}
                subtitle={isSingleDay ? 'Bolsas buenas y scrap del día seleccionado' : 'Tendencia diaria de bolsas buenas y scrap'}
              >
                {isSingleDay
                  ? <Bar data={dailyData} options={dailyOptions} />
                  : <Line data={dailyData} options={dailyOptions} />}
              </ChartCard>
              <ChartCard title="Producción por semana" subtitle="Acumulado por semana ISO">
                <Bar data={barData(analytics.by_week, '#7c3aed')} options={commonOptions} />
              </ChartCard>
              <ChartCard title="Producción por máquina" subtitle="Máquinas ordenadas por mayor producción">
                <Bar data={barData(analytics.by_machine, '#2563eb')} options={machineOptions} />
              </ChartCard>
              <ChartCard title="Producción por turno" subtitle="Comparativo acumulado de turnos">
                <Bar data={barData(analytics.by_shift, '#059669')} options={commonOptions} />
              </ChartCard>
              <ChartCard
                title="Producción por tamaño de bolsa"
                subtitle="Bolsas buenas por Packing Group, en orden S, M, L y XL"
                className="xl:col-span-2"
              >
                <Bar data={barData(analytics.by_packing_group, '#0f766e')} options={commonOptions} />
              </ChartCard>
              <ChartCard title="Producción por Bag Style" subtitle="Bolsas buenas acumuladas por estilo de bolsa">
                <Bar data={barData(analytics.by_bag_style, '#db2777')} options={machineOptions} />
              </ChartCard>
              <ChartCard title="Producción por Bag Size Group" subtitle="Bolsas buenas acumuladas por grupo de tamaño">
                <Bar data={barData(analytics.by_bag_size_group, '#7c3aed')} options={machineOptions} />
              </ChartCard>
            </div>

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <h2 className="text-base font-bold text-slate-900">Detalle por máquina</h2>
                <p className="mt-1 text-xs text-slate-500">Indicadores acumulados para el periodo seleccionado</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-5 py-3 text-left">Máquina</th>
                      <th className="px-4 py-3 text-right">Producción</th>
                      <th className="px-4 py-3 text-right">Prom./reporte</th>
                      <th className="px-4 py-3 text-right">Scrap</th>
                      <th className="px-4 py-3 text-right">Calidad</th>
                      <th className="px-4 py-3 text-right">Uptime</th>
                      <th className="px-4 py-3 text-right">BPM</th>
                      <th className="px-5 py-3 text-right">Reportes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.by_machine.map((machine) => (
                      <tr key={machine.key} className="border-t border-slate-100 hover:bg-blue-50/40">
                        <td className="px-5 py-3 font-bold text-slate-900">{machine.label}</td>
                        <td className="px-4 py-3 text-right font-semibold text-blue-800">{number(machine.production)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{number(machine.average_per_report)}</td>
                        <td className="px-4 py-3 text-right text-amber-700">{number(machine.scrap)}</td>
                        <td className="px-4 py-3 text-right">{number(machine.quality_pct, 2)}%</td>
                        <td className="px-4 py-3 text-right">{number(machine.uptime_pct, 2)}%</td>
                        <td className="px-4 py-3 text-right">{number(machine.bpm, 2)}</td>
                        <td className="px-5 py-3 text-right">{number(machine.reports)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ConversionAnalytics;
